import { useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  createColumnHelper,
  rowPaginationFeature,
  rowSortingFeature,
  tableFeatures,
  useTable,
  type PaginationState,
  type SortingState,
} from "@tanstack/react-table";
import Alert from "@mui/material/Alert";
import Switch from "@mui/material/Switch";
import Tooltip from "@mui/material/Tooltip";
import { useHosts, usePagedPlayers, type PlayerRow } from "../api/queries";
import type { Host } from "../api/models";
import { buildHostFilter, buildNameSearch, PLAYER_SORTS, PLAYERS_PAGE_SIZE, type HostFilter, type PlayerSort } from "../players/player-filters";
import { Breadcrumb } from "../ui/Breadcrumb";
import { PButton } from "../ui/PButton";
import { PMultiSelect } from "../ui/PMultiSelect";
import { PagedDataView, type DataViewLayout } from "../ui/PagedDataView";
import { PSidebar } from "../ui/PSidebar";
import { SortByPopup } from "../ui/SortByPopup";

// PORT OF `src/app/_pages/players`: BREADCRUMB, THE THREE-GROUP TOOLBAR (FILTER / CLEAR,
// SORT DIRECTION / SORT BY, SEARCH), THEN THE PAGED DATA VIEW OF `player-template` CARDS.
// TANSTACK TABLE STAYS AS THE HEADLESS SORT AND PAGING STATE; NOTHING RENDERS A TABLE.

const features = tableFeatures({ rowSortingFeature, rowPaginationFeature });
const helper = createColumnHelper<typeof features, PlayerRow>();

// UNHOSTED IS `hostId === 0` EXACTLY - A MISSING `hostId` RENDERS AS HOSTED, AS IN ANGULAR
const isUnhosted = (r: PlayerRow) => r.hostId === 0;

// COLUMN IDS ARE THE API'S `order_by` VALUES
const columns = helper.columns([
  helper.accessor((r) => r.name, { id: "name" }),
  helper.accessor((r) => r.host?.hostName ?? "", { id: "host_name" }),
]);

const SORT_OPTIONS = PLAYER_SORTS.map((s) => ({ value: s.id, label: s.label }));

type Mode = { kind: "none" } | { kind: "search"; text: string } | { kind: "filter"; filter: HostFilter };

export function PlayersPage() {
  const navigate = useNavigate();
  const hosts = useHosts();
  // THE FILTER'S HOST LIST: DISPLAYED HOSTS LINKED TO A USER (`filter-player.component.ts`)
  const hostOptions = useMemo(() => (hosts.data ?? []).filter((h) => h.userId !== 0), [hosts.data]);

  const [searchText, setSearchText] = useState("");
  // SEARCH AND THE HOST FILTER ARE MUTUALLY EXCLUSIVE ON THE ANGULAR PAGE - APPLYING
  // ONE CLEARS THE OTHER - SO ONE STATE HOLDS WHICHEVER IS IN FORCE
  const [mode, setMode] = useState<Mode>({ kind: "none" });
  const [sorting, setSorting] = useState<SortingState>([{ id: "name", desc: false }]);
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: PLAYERS_PAGE_SIZE });
  const [filterOpen, setFilterOpen] = useState(false);
  const [sortAnchor, setSortAnchor] = useState<HTMLElement | null>(null);
  const [layout, setLayout] = useState<DataViewLayout>("grid");

  const filters = mode.kind === "search" ? buildNameSearch(mode.text) : mode.kind === "filter" ? buildHostFilter(mode.filter) : [];
  const sort = sorting[0] ?? { id: "name", desc: false };
  const players = usePagedPlayers(filters, {
    page: pagination.pageIndex + 1,
    pageSize: PLAYERS_PAGE_SIZE,
    orderBy: sort.id as PlayerSort,
    orderDir: sort.desc ? "desc" : "asc",
  });

  const table = useTable({
    features,
    columns,
    data: players.data?.rows ?? [],
    getRowId: (r, i) => r.playerId ?? r.player_id ?? String(i),
    state: { sorting, pagination },
    // UNLIKE ANGULAR, A SORT CHANGE RETURNS TO PAGE 1 SO IT CANNOT STRAND YOU PAST THE LAST PAGE
    onSortingChange: (updater) => {
      setSorting(updater);
      setPagination((p) => ({ ...p, pageIndex: 0 }));
    },
    onPaginationChange: setPagination,
    manualSorting: true,
    manualPagination: true,
    rowCount: players.data?.totalRows ?? 0,
    enableSortingRemoval: false,
  });

  // SEARCH, APPLY AND CLEAR ALL RESET PAGING AND SORT, AS `setDefaultPaging` DOES -
  // BUT HERE THE SORT CONTROLS RESET WITH THEM, SO THEY NEVER DISAGREE WITH THE REQUEST
  const reset = (next: Mode) => {
    setMode(next);
    setSorting([{ id: "name", desc: false }]);
    setPagination({ pageIndex: 0, pageSize: PLAYERS_PAGE_SIZE });
  };

  const submitSearch = () => {
    reset(searchText ? { kind: "search", text: searchText } : { kind: "none" });
  };

  const openProfile = (r: PlayerRow) => {
    const cardId = r.playerId ?? r.player_id;
    if (!cardId) return;
    void navigate({
      to: "/players/$playerId",
      params: { playerId: cardId },
      // THE ANGULAR PAGE PASSES NO `expectedCmsPlayerId`, SO ITS PROFILE RESOLVES BY
      // (NON-UNIQUE) CARD ID; THE PILOT NAMES THE PLAYER IT MEANT, AS IN PLAY DOES
      state: r.cmsPlayerId ? { expectedCmsPlayerId: r.cmsPlayerId } : {},
    });
  };

  const sortLabel = PLAYER_SORTS.find((o) => o.id === sort.id)?.label ?? "Player";
  const data = players.data;

  return (
    <div className="parent">
      <div className="child">
        <div className="flex flex-row align-items-center justify-content-between">
          <Breadcrumb items={[{ label: "Players" }]} />
        </div>

        <div className="mb-2 p-1">
          <div className="grid grid-nogutter">
            <div className="col-6 md:col-4 p-0">
              <div className="p-inputgroup flex">
                <Tooltip title="Filter" placement="bottom">
                  <PButton icon="filter" ariaLabel="Filter" onClick={() => setFilterOpen((o) => !o)} />
                </Tooltip>
                <Tooltip title="Clear Filter" placement="bottom">
                  <span>
                    <PButton
                      icon="filter-slash"
                      ariaLabel="Clear Filter"
                      disabled={mode.kind === "none"}
                      onClick={() => {
                        setSearchText("");
                        reset({ kind: "none" });
                      }}
                      sx={{ backgroundColor: "var(--primary-700)" }}
                    />
                  </span>
                </Tooltip>
              </div>
            </div>
            <div className="col-6 md:col-4 p-0">
              <div className="p-inputgroup flex justify-content-end md:justify-content-center">
                <Tooltip title="Sort Direction" placement="bottom">
                  <PButton
                    icon={sort.desc ? "sort-amount-up-alt" : "sort-amount-down-alt"}
                    ariaLabel="Sort Direction"
                    onClick={() => table.setSorting([{ id: sort.id, desc: !sort.desc }])}
                  />
                </Tooltip>
                <Tooltip title="Sort By" placement="bottom">
                  <PButton label={sortLabel} onClick={(e) => setSortAnchor(e.currentTarget)} />
                </Tooltip>
              </div>
            </div>
            <div className="col-12 md:col-4 mt-2 md:mt-0 p-0">
              <div className="p-inputgroup flex">
                <input
                  type="text"
                  className="p-inputtext bg-input-field"
                  placeholder="Search"
                  aria-label="Search players"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") submitSearch();
                  }}
                />
                <PButton icon="search" ariaLabel="Search" onClick={submitSearch} />
              </div>
            </div>
          </div>
        </div>
        {players.isError && (
          <Alert severity="warning" className="mb-2">
            Players could not be loaded. Please try again.
          </Alert>
        )}
      </div>

      <div className="child-grow mt-3 card p-0 shadow-1 overflow-x-hidden overflow-y-auto">
        <PagedDataView
          rows={table.getRowModel().rows.map((r) => r.original)}
          page={pagination.pageIndex + 1}
          totalPages={Math.max(1, data?.totalPages ?? 1)}
          totalRows={data?.totalRows ?? 0}
          onPage={(p) => table.setPageIndex(p - 1)}
          layout={layout}
          onLayout={setLayout}
          emptyMessage="No players available"
          rowKey={(r) => r.playerId ?? r.player_id ?? r.name ?? ""}
          render={(r, l) => <PlayerCard player={r} layout={l} onOpen={() => openProfile(r)} />}
        />
      </div>

      <SortByPopup
        anchorEl={sortAnchor}
        onClose={() => setSortAnchor(null)}
        value={sort.id}
        options={SORT_OPTIONS}
        onChange={(v) => table.setSorting([{ id: v, desc: sort.desc }])}
      />

      <HostFilterSidebar
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        hosts={hostOptions}
        current={mode.kind === "filter" ? mode.filter : null}
        onApply={(filter) => {
          setSearchText("");
          reset({ kind: "filter", filter });
          setFilterOpen(false);
        }}
      />
    </div>
  );
}

// `player-template`: THE GRID CARD IS `pl-2 pr-2 border-1 surface-border surface-card
// border-round`; THE LIST ROW HAS NO CARD. CARD ICON PRIMARY WHEN HOSTED, `--gray-500` WHEN
// NOT; "HOST: NAME" OR "HOST: n/a" IN `text-sm` BESIDE A LARGE ROUNDED TEXT CHEVRON BUTTON.
function PlayerCard({ player: r, layout, onOpen }: { player: PlayerRow; layout: DataViewLayout; onOpen: () => void }) {
  const body = (
    <div>
      <div className="flex flex-row align-items-center">
        {r.cmsPlayerId && <span className={`material-symbols-rounded ${isUnhosted(r) ? "color-gray-500" : "color-primary"}`}>credit_card</span>}
        <button type="button" className="text-overflow-ellipsis font-bold ml-3 hover-link" onClick={onOpen}>
          {(r.name ?? "").toUpperCase()}
        </button>
      </div>
      <div className="w-full flex flex-row align-items-center justify-content-between gap-2">
        <div className="text-overflow-ellipsis text-sm">{isUnhosted(r) ? "HOST: n/a" : `HOST: ${(r.host?.hostName ?? "").toUpperCase()}`}</div>
        <div>
          <PButton icon="chevron-circle-right" rounded variant="text" size="lg" ariaLabel="Open player" onClick={onOpen} />
        </div>
      </div>
    </div>
  );
  return layout === "grid" ? <div className="pl-2 pr-2 border-1 surface-border surface-card border-round flex flex-column">{body}</div> : body;
}

// THE ONLY PART OF `advanced-filters` THE PLAYERS PAGE SHOWS (`showFilterPlayerOnly`): A
// `p-toolbar mt-3` WITH APPLY AT THE RIGHT, THEN THE `filter-player` CARD - "PLAYER HOST"
// (`font-bold text-lg`) WITH ITS ENABLE SWITCH, AN `mt-5` HOST MULTI-SELECT AND AN `mt-5`
// UNHOSTED SWITCH, BOTH DISABLED UNTIL THE CARD IS ENABLED. HOSTS AND UNHOSTED ARE MUTUALLY
// EXCLUSIVE, AND APPLY NEEDS AN ENABLED CARD WITH ONE OF THEM (`can_apply_filter`).
function HostFilterSidebar(props: {
  open: boolean;
  onClose: () => void;
  hosts: Host[];
  current: HostFilter | null;
  onApply: (f: HostFilter) => void;
}) {
  const { open, onClose, hosts, current, onApply } = props;
  const [enabled, setEnabled] = useState(false);
  const [hostIds, setHostIds] = useState<number[]>([]);
  const [unhosted, setUnhosted] = useState(false);

  // RE-SEED FROM WHAT IS APPLIED EACH TIME THE SIDEBAR OPENS, SO A CANCELED EDIT
  // DOES NOT LINGER (ANGULAR'S SHARED, MUTATED FILTER OBJECT DOES LET IT LINGER)
  const [seededFor, setSeededFor] = useState<boolean>(false);
  if (open && !seededFor) {
    setSeededFor(true);
    setEnabled(current !== null);
    setUnhosted(current?.kind === "unhosted");
    setHostIds(current?.kind === "hosts" ? current.hostIds : []);
  } else if (!open && seededFor) {
    setSeededFor(false);
  }

  const canApply = enabled && (unhosted || hostIds.length > 0);

  return (
    <PSidebar open={open} onClose={onClose} position="left" modal={false} width={{ xs: "100%", md: "30rem" }} ariaLabel="Filters">
      <div className="p-toolbar mt-3">
        <div className="w-full flex justify-content-end">
          <PButton
            label="Apply"
            icon="filter"
            hostElement
            disabled={!canApply}
            onClick={() => onApply(unhosted ? { kind: "unhosted" } : { kind: "hosts", hostIds })}
          />
        </div>
      </div>
      <div className="mt-3">
        <div className="card w-full">
          <div className="flex flex-row align-items-center justify-content-between">
            <div className="font-bold text-lg">Player Host</div>
            <div>
              <Switch
                checked={enabled}
                onChange={(e) => {
                  setEnabled(e.target.checked);
                  if (!e.target.checked) setHostIds([]);
                }}
                slotProps={{ input: { "aria-label": "Filter by player host" } }}
              />
            </div>
          </div>
          <div className="mt-5">
            <PMultiSelect
              disabled={!enabled}
              value={hostIds}
              options={hosts.map((h) => ({ label: h.hostName, value: h.id }))}
              placeholder="Select Hosts"
              onChange={(v) => {
                setHostIds(v);
                if (v.length > 0) setUnhosted(false);
              }}
            />
          </div>
          <div className="mt-5 flex flex-row align-items-center justify-content-between">
            <div className="font-bold">Unhosted</div>
            <Switch
              disabled={!enabled}
              checked={unhosted}
              onChange={(e) => {
                setUnhosted(e.target.checked);
                if (e.target.checked) setHostIds([]);
              }}
              slotProps={{ input: { "aria-label": "Unhosted" } }}
            />
          </div>
        </div>
      </div>
    </PSidebar>
  );
}
